// src/components/assessment/RadarTab.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { catalog } from "../../content/catalog.v1";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export function pct(done, total) {
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

export function statusClass(p) {
  if (p >= 50) return "status-dot status-dot--green";
  return "status-dot status-dot--orange";
}

export function buildRadarModel(themes, checkedActions) {
  const items = themes.map((theme) => {
    const a1 = theme.levels?.level1?.acties || [];
    const a2 = theme.levels?.level2?.acties || [];
    const all = [...a1, ...a2];
    const total = all.length;
    const done = all.reduce((acc, a) => acc + (checkedActions?.[a.id] ? 1 : 0), 0);
    return {
      id: theme.id,
      icon: theme.icon,
      shortName: theme.shortName || theme.title,
      title: theme.title,
      pct: pct(done, total),
    };
  });
  return {
    labels: items.map((x) => x.shortName),
    values: items.map((x) => x.pct),
    items,
  };
}

export default function RadarTab({ checkedActions }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const model = useMemo(
    () => buildRadarModel(catalog.themes, checkedActions),
    [checkedActions]
  );

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const ctx = canvasRef.current.getContext("2d");

    chartRef.current = new Chart(ctx, {
      type: "radar",
      data: {
        labels: model.labels,
        datasets: [
          {
            label: "",
            data: model.values,
            fill: true,
            borderColor: "rgba(37, 99, 235, 1)",
            borderWidth: 3,
            backgroundColor: "rgba(37, 99, 235, 0.12)",
            pointBackgroundColor: "rgba(37, 99, 235, 1)",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 5,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (c) => `${c.parsed.r}%`,
            },
          },
        },
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 25,
              callback: (v) => `${v}`,
              backdropColor: "transparent",
            },
            pointLabels: {
              font: { size: 12, weight: "600" },
            },
            grid: { circular: false },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [model]);

  return (
    <div className="space-y-4">
      {/* === HEADER BOVEN RADAR === */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#ffffff" }}>
            RADAROVERZICHT EA-VOLWASSENHEID
          </div>
          <div className="text-sm" style={{ color: "#ffffff" }}>
            De radar toont per EA-onderwerp de gecombineerde voortgang van Fase 1 en Fase 2 (0–100%).
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            style={{
              background: "#ffffff",
              color: "#27435d",
              boxShadow: "0 3px 10px rgba(0,0,0,0.22)",
              letterSpacing: "0.01em",
            }}
          >
            <span>Totaal gemiddeld</span>
            <span
              style={{
                background: "#31719D",
                color: "#ffffff",
                borderRadius: "999px",
                padding: "1px 9px",
                fontWeight: 800,
                fontSize: "11px",
                letterSpacing: "0.03em",
              }}
            >
              {(model.values.reduce((a, b) => a + b, 0) / (model.values.length || 1)).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>

      {/* === RADAR CONTAINER === */}
      <div className="radar-shell">
        <div className="radar-layout">
          <div className="radar-left">
            <div className="radar-chart-wrap">
              <canvas ref={canvasRef} />
            </div>
          </div>

          <div className="radar-right">
            {model.items.map((it) => (
              <div key={it.id} className="radar-item-card">
                <div className="radar-item-icon">
                  {it.icon}
                </div>

                <div className="radar-item-text">
                  <div className="radar-item-name">
                    {it.shortName}
                  </div>
                  <div className="radar-item-sub">
                    {it.title}
                  </div>
                </div>

                <div className="radar-item-score">
                  <span className={statusClass(it.pct)} />
                  <span className="radar-item-pct">
                    {it.pct}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}